import type {ICarFilter} from "~/interfaces/ICarFilter";
import type {OrderBy} from "~/interfaces/OrderBy";

export const useCarFilters = () => {
    // Data
    const colors: Record<'exterior' | 'interior', any[]> = {
        exterior: [
            {name: 'Black', rgb: '0,0,0'},
            {name: 'Charcoal Grey', rgb: '74,74,74'},
            {name: 'Grey', rgb: '128,128,128'},
            {name: 'Silver', rgb: '192,192,192'},
            {name: 'White', rgb: '255,255,255'},
            {name: 'Pearl White', rgb: '250,240,230'},
            {name: 'Beige', rgb: '245,245,220'},
            {name: 'Gold', rgb: '255,215,0'},
            {name: 'Tan', rgb: '210,180,140'},
            {name: 'Brown', rgb: '139,69,19'},
            {name: 'Burgundy', rgb: '128,0,32'},
            {name: 'Red', rgb: '255,0,0'},
            {name: 'Orange', rgb: '255,165,0'},
            {name: 'Yellow', rgb: '255,255,0'},
            {name: 'Dark Green', rgb: '0,100,0'},
            {name: 'Bright Green', rgb: '0,255,0'},
            {name: 'Light Green', rgb: '144,238,144'},
            {name: 'Dark Blue', rgb: '0,0,139'},
            {name: 'Bright Blue', rgb: '30,144,255'},
            {name: 'Light Blue', rgb: '173,216,230'},
            {name: 'Purple', rgb: '128,0,128'}
        ],
        interior: [
            {name: 'Black', rgb: '0,0,0'},
            {name: 'Charcoal Grey', rgb: '74,74,74'},
            {name: 'Grey', rgb: '128,128,128'},
            {name: 'White', rgb: '255,255,255'},
            {name: 'Beige', rgb: '245,245,220'},
            {name: 'Brown', rgb: '139,69,19'},
            {name: 'Red', rgb: '255,0,0'}
        ]
    };
    const drive_trains = ['FWD', 'RWD', 'AWD', '4WD'];
    const transmissions = ['Automática', 'Manual', 'CVT', 'DCT', 'Semi-Automática'];
    const fuel_types = ['Gasolina', 'Diesel', 'Híbrido', 'Eléctrico'];
    const interior_types = [
        'Tela Estándar',
        'Tela Sintética',
        'Vinilo',
        'Alcántara',
        'Cuero Sintético',
        'Cuero Premium',
        'Premium',
        'Estándar',
    ];
    const body_styles = [
        'SUV',
        'Pickup',
        'Convertible',
        'Hatchback',
        'Sedan',
        'Coupe',
        'Van',
        'Truck',
        'Minivan',
        'Wagon',
    ];
    const prices_min = [
        {value: 0, label: "El más bajo"},
        {value: 2000, label: "$ 2,000"},
        {value: 4000, label: "$ 4,000"},
        {value: 6000, label: "$ 6,000"},
        {value: 8000, label: "$ 8,000"},
        {value: 10000, label: "$ 10,000"},
        {value: 15000, label: "$ 15,000"},
        {value: 20000, label: "$ 20,000"},
        {value: 25000, label: "$ 25,000"},
        {value: 30000, label: "$ 30,000"},
        {value: 35000, label: "$ 35,000"},
        {value: 40000, label: "$ 40,000"},
        {value: 45000, label: "$ 45,000"},
        {value: 50000, label: "$ 50,000"},
        {value: 60000, label: "$ 60,000"},
        {value: 70000, label: "$ 70,000"},
        {value: 80000, label: "$ 80,000"},
        {value: 90000, label: "$ 90,000"},
        {value: 100000, label: "$ 100,000"},
        {value: 125000, label: "$ 125,000"},
        {value: 150000, label: "$ 150,000"},
        {value: 175000, label: "$ 175,000"},
    ];
    const prices_max = [
        {value: 999999999, label: "El más alto"},
        {value: 2000, label: "$ 2,000"},
        {value: 4000, label: "$ 4,000"},
        {value: 6000, label: "$ 6,000"},
        {value: 8000, label: "$ 8,000"},
        {value: 10000, label: "$ 10,000"},
        {value: 15000, label: "$ 15,000"},
        {value: 20000, label: "$ 20,000"},
        {value: 25000, label: "$ 25,000"},
        {value: 30000, label: "$ 30,000"},
        {value: 35000, label: "$ 35,000"},
        {value: 40000, label: "$ 40,000"},
        {value: 45000, label: "$ 45,000"},
        {value: 50000, label: "$ 50,000"},
        {value: 60000, label: "$ 60,000"},
        {value: 70000, label: "$ 70,000"},
        {value: 80000, label: "$ 80,000"},
        {value: 90000, label: "$ 90,000"},
        {value: 100000, label: "$ 100,000"},
        {value: 125000, label: "$ 125,000"},
        {value: 150000, label: "$ 150,000"},
        {value: 175000, label: "$ 175,000"},
    ];
    const mileageFilters = [
        {value: 0, label: "0 mi."},
        {value: 5000, label: "5K mi."},
        {value: 10000, label: "10K mi."},
        {value: 20000, label: "20K mi."},
        {value: 30000, label: "30K mi."},
        {value: 50000, label: "50K mi."},
        {value: 75000, label: "75K mi."},
        {value: 100000, label: "100K mi."},
        {value: 150000, label: "150K mi."},
        {value: 200000, label: "200K mi."},
        {value: 250000, label: "250K mi."}
    ];
    const orders: OrderBy[] = [
        {
            attribute: 'listings.created_at',
            direction: 'DESC',
            label: 'Más Recientes'
        },
        {
            attribute: 'visit_count',
            direction: 'DESC',
            label: 'Más Vistos'
        },
        {
            attribute: 'follower_count',
            direction: 'DESC',
            label: 'Mayor número de Favoritos'
        },
        {
            attribute: 'price',
            direction: 'ASC',
            label: 'Menor Precio Primero'
        },
        {
            attribute: 'price',
            direction: 'DESC',
            label: 'Mayor Precio Primero'
        }
    ]
    const years = [
        1960,
        1970,
        1980,
        1990,
        2000,
        2001,
        2002,
        2003,
        2004,
        2005,
        2006,
        2007,
        2008,
        2009,
        2010,
        2011,
        2012,
        2013,
        2014,
        2015,
        2016,
        2017,
        2018,
        2019,
        2020,
        2021,
        2022,
        2023,
        2024,
        2025,
    ].reverse()

    const getBaseFilters = (): ICarFilter => ({
        condition: {
            type: 'complete-text-search',
            value: '',
            attribute: 'listings.sub_category'
        },
        make: {
            type: 'complete-text-search',
            value: '',
            attribute: 'cars.make'
        },
        model: {
            type: 'complete-text-search',
            value: '',
            attribute: 'cars.model'
        },
        price: {
            type: 'minmax',
            value: {
                min: '',
                max: ''
            },
            attribute: 'price'
        },
        millage: {
            type: 'minmax',
            value: {
                min: '',
                max: ''
            },
            attribute: 'cars.millage'
        },
        year: {
            type: 'minmax',
            value: {
                min: '',
                max: ''
            },
            attribute: 'cars.year'
        },
        color: {
            type: 'in-match',
            value: [],
            attribute: 'cars.color'
        },
        interior_color: {
            type: 'in-match',
            value: [],
            attribute: 'cars.interior_color'
        },
        interior_type: {
            type: 'in-match',
            value: [],
            attribute: 'cars.interior_type'
        },
        body_style: {
            type: 'in-match',
            value: [],
            attribute: 'cars.body_style'
        },
        transmission: {
            type: 'in-match',
            value: [],
            attribute: 'cars.transmission'
        },
        drive_train: {
            type: 'in-match',
            value: [],
            attribute: 'cars.drive_train'
        },
        fuel_type: {
            type: 'in-match',
            value: [],
            attribute: 'cars.fuel_type'
        }
    })
    const getValidFilters = (filters: ICarFilter) => {
        const validFilters = []
        if (!!filters.condition && !!filters.condition.value && filters.condition.value !== '') {
            validFilters.push(filters.condition)
        }
        if (!!filters.make && !!filters.make.value && filters.make.value !== '') {
            validFilters.push(filters.make)
        }
        if (!!filters.model && !!filters.model.value && filters.model.value !== '') {
            validFilters.push(filters.model)
        }
        if (!!filters.price && !!filters.price.value && (!!filters.price.value.min || !!filters.price.value.max) && filters.price.value.min !== '' || filters.price.value.max !== '') {
            validFilters.push(filters.price)
        }
        if (!!filters.millage && !!filters.millage.value && (!!filters.millage.value.min || !!filters.millage.value.max) && filters.millage.value.min !== '' || filters.millage.value.max !== '') {
            validFilters.push(filters.millage)
        }
        if (!!filters.year && !!filters.year.value && (!!filters.year.value.min || !!filters.year.value.max) && filters.year.value.min !== '' || filters.year.value.max !== '') {
            validFilters.push(filters.year)
        }
        if (!!filters.body_style && !!filters.body_style.value && filters.body_style.value.length > 0) {
            validFilters.push(filters.body_style)
        }
        if (!!filters.fuel_type && !!filters.fuel_type.value && filters.fuel_type.value.length > 0) {
            validFilters.push(filters.fuel_type)
        }
        if (!!filters.transmission && !!filters.transmission.value && filters.transmission.value.length > 0) {
            validFilters.push(filters.transmission)
        }
        if (!!filters.drive_train && !!filters.drive_train.value && filters.drive_train.value.length > 0) {
            validFilters.push(filters.drive_train)
        }
        if (!!filters.color && !!filters.color.value && filters.color.value.length > 0) {
            validFilters.push(filters.color)
        }
        if (!!filters.interior_color && !!filters.interior_color.value && filters.interior_color.value.length > 0) {
            validFilters.push(filters.interior_color)
        }
        if (!!filters.interior_type && !!filters.interior_type.value && filters.interior_type.value.length > 0) {
            validFilters.push(filters.interior_type)
        }

        return validFilters

    }
    const hasFilterValues = computed(() => Object.values(carFilters.value).some((filter: any) => {
        if (filter.type) {
            if (filter.type == 'complete-text-search') {
                return !!filter.value && filter.value !== ''
            } else if (filter.type == 'minmax') {
                return (!!filter.value.min || !!filter.value.max) && (filter.value.min !== '' || filter.value.max !== '')
            } else if (filter.type == 'in-match') {
                return filter.value.length > 0
            } else {
                return false
            }
        } else {
            return filter !== ''
        }
    }))

    // Model
    const carFilters = ref<ICarFilter>(getBaseFilters())

    return {
        carFilters,
        orders,
        colors,
        drive_trains,
        transmissions,
        fuel_types,
        interior_types,
        body_styles,
        hasFilterValues,
        prices_min,
        prices_max,
        mileageFilters,
        years,
        getBaseFilters,
        getValidFilters
    }
}